'use client'

import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import type { CoinMarketData } from '@/lib/coingecko'
import { formatPrice, formatCompact, formatChange } from '@/lib/formatters'
import { Sparkline } from './Sparkline'

// =============================================================
// PriceTable — client component
// Handles sorting, search, row rendering
// =============================================================

interface PriceTableProps {
  coins: CoinMarketData[]
}

function SortIcon({ active, direction }: { active: boolean; direction: string }) {
  return (
    <svg
      width="10"
      height="14"
      viewBox="0 0 10 14"
      className={`ml-1 inline-block transition-opacity ${active ? 'opacity-100' : 'opacity-0 group-hover:opacity-40'}`}
    >
      <path d="M5 0L9.5 5.5H0.5L5 0Z" fill={active && direction === 'asc' ? '#a1a1aa' : '#52525b'} />
      <path d="M5 14L0.5 8.5H9.5L5 14Z" fill={active && direction === 'desc' ? '#a1a1aa' : '#52525b'} />
    </svg>
  )
}

function ChangeArrow({ positive }: { positive: boolean }) {
  return positive ? (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor" className="inline -mt-px">
      <path d="M4 1L7 5H1L4 1Z" />
    </svg>
  ) : (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor" className="inline -mt-px">
      <path d="M4 7L1 3H7L4 7Z" />
    </svg>
  )
}

type SortKey = 'market_cap_rank' | 'name' | 'current_price' | 'price_change_percentage_24h' | 'market_cap'

const columns: { key: SortKey | 'sparkline'; label: string; align: string; sortable?: boolean }[] = [
  { key: 'market_cap_rank', label: '#', align: 'text-left' },
  { key: 'name', label: 'Name', align: 'text-left' },
  { key: 'current_price', label: 'Price', align: 'text-right' },
  { key: 'price_change_percentage_24h', label: '24h %', align: 'text-right' },
  { key: 'market_cap', label: 'Market cap', align: 'text-right' },
  { key: 'sparkline', label: 'Last 7 days', align: 'text-right', sortable: false },
]

export function PriceTable({ coins }: PriceTableProps) {
  const [search, setSearch] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const [sortKey, setSortKey] = useState<SortKey>('market_cap_rank')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (searchOpen && searchRef.current) searchRef.current.focus()
  }, [searchOpen])

  const handleSort = useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortKey(key)
        setSortDir(key === 'market_cap_rank' ? 'asc' : 'desc')
      }
    },
    [sortKey],
  )

  const filtered = useMemo(() => {
    let result = [...coins]
    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter(
        (c) => c.name.toLowerCase().includes(q) || c.symbol.toLowerCase().includes(q),
      )
    }
    result.sort((a, b) => {
      const av = a[sortKey] ?? 0
      const bv = b[sortKey] ?? 0
      if (sortDir === 'asc') return av > bv ? 1 : -1
      return av < bv ? 1 : -1
    })
    return result
  }, [coins, search, sortKey, sortDir])

  return (
    <div className="border border-zinc-800 overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-800 bg-black">
        <div
          className="text-zinc-400 text-xs"
          style={{ fontFamily: "var(--font-saira), 'Saira', sans-serif", fontSize: 11 }}
        >
          {filtered.length} coin{filtered.length !== 1 ? 's' : ''}
          {search && (
            <span className="text-zinc-500">
              {' '}· results for &ldquo;{search}&rdquo;
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {searchOpen && (
            <input
              ref={searchRef}
              type="text"
              placeholder="Search name or symbol..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onBlur={() => { if (!search) setSearchOpen(false) }}
              onKeyDown={(e) => { if (e.key === 'Escape') { setSearch(''); setSearchOpen(false) } }}
              className="bg-black border border-zinc-700 px-2.5 py-1 text-xs text-zinc-300 placeholder-zinc-500 focus:outline-none focus:border-zinc-500"
              style={{ width: 200 }}
            />
          )}
          <button
            onClick={() => { if (searchOpen && search) setSearch(''); setSearchOpen(!searchOpen) }}
            className={`p-1.5 transition-colors ${searchOpen ? 'bg-zinc-900 text-zinc-300' : 'text-zinc-400 hover:text-zinc-300'}`}
            title="Search coins"
            aria-label="Toggle search"
          >
            {searchOpen && search ? (
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="3" y1="3" x2="11" y2="11" /><line x1="11" y1="3" x2="3" y2="11" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full" style={{ minWidth: 700 }}>
          <thead>
            <tr className="border-b border-zinc-800 bg-black">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-3 text-xs uppercase tracking-widest text-zinc-400 font-medium ${col.align} ${
                    col.sortable !== false ? 'cursor-pointer group select-none hover:text-zinc-400' : ''
                  }`}
                  style={{ fontFamily: "var(--font-saira), 'Saira', sans-serif", fontSize: 10 }}
                  onClick={col.sortable !== false ? () => handleSort(col.key as SortKey) : undefined}
                >
                  <span className="inline-flex items-center">
                    {col.label}
                    {col.sortable !== false && <SortIcon active={sortKey === col.key} direction={sortDir} />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((coin) => {
              const positive = coin.price_change_percentage_24h >= 0
              const sparklineData = coin.sparkline_in_7d?.price || []

              return (
                <tr
                  key={coin.id}
                  className="border-b border-zinc-800 hover:bg-zinc-900/50 transition-colors cursor-pointer group/row"
                >
                  <td className="px-4 py-3.5 text-zinc-400 text-sm">{coin.market_cap_rank}</td>
                  <td className="px-4 py-3.5">
                    <Link href={`/prices/${coin.id}`} prefetch={false} className="flex items-center gap-2.5">
                      {coin.image && (
                        <Image
                          src={coin.image}
                          alt={coin.name}
                          width={28}
                          height={28}
                          className="rounded-full flex-shrink-0"
                        />
                      )}
                      <div className="flex items-baseline gap-2">
                        <span className="text-white text-sm font-medium group-hover/row:text-green-400 transition-colors">
                          {coin.name}
                        </span>
                        <span className="text-zinc-400 text-xs uppercase">{coin.symbol}</span>
                      </div>
                    </Link>
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <span className="text-white text-sm font-medium tabular-nums">{formatPrice(coin.current_price)}</span>
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <span className={`text-sm font-medium tabular-nums inline-flex items-center gap-0.5 ${positive ? 'text-green-500' : 'text-red-500'}`}>
                      <ChangeArrow positive={positive} />{formatChange(coin.price_change_percentage_24h)}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <span className="text-zinc-300 text-sm tabular-nums">{formatCompact(coin.market_cap)}</span>
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <div className="flex justify-end"><Sparkline data={sparklineData} /></div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="py-12 text-center text-zinc-400 text-sm bg-black">
          {search ? `No coins matching "${search}"` : 'No data available'}
        </div>
      )}
    </div>
  )
}